import { useEffect, useState } from "react";
import { CircleNotch, EnvelopeSimple } from "@phosphor-icons/react";
import { Button } from "../design-system";
import { logger } from "../services/telemetry";
import { useAuthMethods, useAuthUser } from "./session";
import { trackButtonPressed } from "../analytics/tracking";
import { resendErrorMessage } from "./authErrors";
import { logout } from "./logout";
import { APP_BASENAME, LOGIN_PATH } from "./paths";
import { AuthCard, AuthCta, AuthHeading, AuthFrame } from "./AuthShared";

/* Shown to a signed-in-but-unverified email/password user (the "unverified"
 * status — see session.tsx). Their session cookie is set, so they can resend
 * the verification email, but the gate keeps them out of the dashboard until
 * the email is verified.
 *
 * Verification itself happens on Firebase's hosted action page (the identity
 * service emails a Firebase verify link). Once verified there, the user clicks
 * "I've verified" here, which reloads: bootstrap re-fetches /identity/user,
 * which re-checks the live Firebase status and now reports the email verified,
 * flipping the status to "authenticated" so the gate lets them in. */

// Matches the identity service's resend rate limit (1/min).
const RESEND_COOLDOWN_S = 60;

export function CheckEmailScreen() {
  const user = useAuthUser();
  const { resendVerification } = useAuthMethods();
  const [cooldown, setCooldown] = useState(0);
  const [resending, setResending] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (cooldown <= 0) return;

    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const handleResend = async () => {
    trackButtonPressed("Resend email");
    setErr(null);
    setNotice(null);
    setResending(true);
    try {
      await resendVerification();
      setNotice("Sent. Check your inbox — and your spam folder.");
      setCooldown(RESEND_COOLDOWN_S);
    } catch (e) {
      logger.error(
        e instanceof Error ? e : new Error(String(e)),
        "Resend verification failed",
      );
      setErr(resendErrorMessage(e));
    } finally {
      setResending(false);
    }
  };

  const handleSwitchAccount = async () => {
    if (switching) return;

    trackButtonPressed("Use a different account");
    setSwitching(true);
    // logout ends the identity session + clears Firebase; navigate to a clean
    // /login (not reload) so the now-anonymous gate lands on a bare login URL
    // rather than carrying a ?redirect= back to where they signed out from.
    await logout();
    window.location.assign(`${APP_BASENAME}${LOGIN_PATH}`);
  };

  return (
    <AuthFrame>
      <div className="onboarding onboarding-card w-full">
        <AuthCard>
          <div className="flex flex-col items-center gap-6 text-center">
            <div
              className="flex size-12 items-center justify-center [&_svg]:size-6"
              style={{
                background: "var(--petal)",
                color: "var(--carmine)",
                borderRadius: "var(--radius-pill)",
              }}
            >
              <EnvelopeSimple />
            </div>

            <div className="flex flex-col gap-1.5">
              <AuthHeading>Check your email</AuthHeading>
              <p className="t-body-sm">
                We sent a verification link to{" "}
                <span style={{ color: "var(--fg-1)", fontWeight: 600 }}>
                  {user?.userEmail ?? "your email"}
                </span>
                . Open it to verify your account, then continue.
              </p>
            </div>

            {notice && (
              <p className="text-body-sm text-success-fg" role="status">
                {notice}
              </p>
            )}
            {err && (
              <div
                role="alert"
                className="w-full rounded-md border border-error-border bg-error-bg px-3 py-2.5 text-left"
              >
                <span className="text-body-sm text-error-fg">{err}</span>
              </div>
            )}

            <div className="flex w-full flex-col gap-2.5">
              <AuthCta
                className="w-full"
                onClick={() => {
                  trackButtonPressed("I've verified my email");
                  window.location.reload();
                }}
              >
                I&apos;ve verified my email
              </AuthCta>
              <Button
                variant="secondary"
                size="lg"
                className="w-full"
                onClick={handleResend}
                disabled={resending || cooldown > 0}
                iconLeft={
                  resending ? (
                    <CircleNotch className="animate-spin" />
                  ) : undefined
                }
              >
                {cooldown > 0 ? `Resend email (${cooldown}s)` : "Resend email"}
              </Button>
            </div>

            <button
              type="button"
              onClick={() => void handleSwitchAccount()}
              disabled={switching}
              className="t-body-sm font-medium no-underline hover:underline disabled:cursor-not-allowed disabled:opacity-50"
              style={{ color: "var(--fg-1)" }}
            >
              Use a different account
            </button>
          </div>
        </AuthCard>
      </div>
    </AuthFrame>
  );
}
